import React, { useState, useEffect } from 'react';
import './AuthScreen.css';

const AuthScreen = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [authMethod, setAuthMethod] = useState('password');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    otp: ['', '', '', '', '', ''],
    name: ''
  });
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [timer, setTimer] = useState(0);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Timer for OTP resend
  useEffect(() => {
    if (timer > 0) {
      const interval = setInterval(() => {
        setTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [timer]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleOtpChange = (index, value) => {
    if (value.length <= 1 && /^[0-9]*$/.test(value)) {
      const newOtp = [...formData.otp];
      newOtp[index] = value;
      setFormData(prev => ({ ...prev, otp: newOtp }));

      // Auto-focus next input
      if (value && index < 5) {
        document.getElementById(`otp-${index + 1}`)?.focus();
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !formData.otp[index] && index > 0) {
      document.getElementById(`otp-${index - 1}`)?.focus();
    }
  };

  const handleSendOtp = async () => {
    const contact = authMethod === 'email' ? formData.email : formData.phone;
    if (!contact) {
      setErrors({ [authMethod]: `Please enter ${authMethod}` });
      return;
    }

    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIsOtpSent(true);
      setTimer(30);
      setIsLoading(false);
    }, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Simulate API call
    setTimeout(() => {
      console.log('Form submitted:', formData);
      setIsLoading(false);
      // Handle success/error
    }, 1500);
  };

  const switchMode = () => {
    setIsLogin(!isLogin);
    setErrors({});
    setIsOtpSent(false);
  };

  const switchAuthMethod = (method) => {
    setAuthMethod(method);
    setIsOtpSent(false);
    setErrors({});
  };

  return (
    <div className="auth-container">
      <div className="auth-wrapper">
        <div className={`auth-box ${!isLogin ? 'register-mode' : ''}`}>
          {/* Left Side - Form */}
          <div className="auth-form-container">
            <div className="auth-header">
              <h2>{isLogin ? 'Welcome Back!' : 'Create Account'}</h2>
              <p>{isLogin ? 'Sign in to continue' : 'Sign up to get started'}</p>
            </div>

            {/* Auth Method Toggle */}
            <div className="auth-method-toggle">
              <button
                className={`method-btn ${authMethod === 'password' ? 'active' : ''}`}
                onClick={() => switchAuthMethod('password')}
              >
                <PasswordIcon />
                Password
              </button>
              <button
                className={`method-btn ${authMethod === 'email' ? 'active' : ''}`}
                onClick={() => switchAuthMethod('email')}
              >
                <EmailIcon />
                Email OTP
              </button>
              <button
                className={`method-btn ${authMethod === 'phone' ? 'active' : ''}`}
                onClick={() => switchAuthMethod('phone')}
              >
                <PhoneIcon />
                SMS OTP
              </button>
            </div>

            <form onSubmit={handleSubmit} className="auth-form">
              {/* Name Field (Register only) */}
              {!isLogin && (
                <div className="form-group animate-in">
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder=" "
                    className={errors.name ? 'error' : ''}
                    required
                  />
                  <label>Full Name</label>
                  {errors.name && <span className="error-text">{errors.name}</span>}
                </div>
              )}

              {/* Email Field */}
              {(authMethod === 'password' || authMethod === 'email') && (
                <div className="form-group animate-in">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder=" "
                    className={errors.email ? 'error' : ''}
                    required
                  />
                  <label>Email Address</label>
                  {errors.email && <span className="error-text">{errors.email}</span>}
                </div>
              )}

              {/* Phone Field */}
              {authMethod === 'phone' && (
                <div className="form-group animate-in">
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder=" "
                    className={errors.phone ? 'error' : ''}
                    required
                  />
                  <label>Phone Number</label>
                  {errors.phone && <span className="error-text">{errors.phone}</span>}
                </div>
              )}

              {/* Password Fields */}
              {authMethod === 'password' && (
                <>
                  <div className="form-group animate-in">
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      placeholder=" "
                      className={errors.password ? 'error' : ''}
                      required
                    />
                    <label>Password</label>
                    {errors.password && <span className="error-text">{errors.password}</span>}
                  </div>

                  {!isLogin && (
                    <div className="form-group animate-in">
                      <input
                        type="password"
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        placeholder=" "
                        className={errors.confirmPassword ? 'error' : ''}
                        required
                      />
                      <label>Confirm Password</label>
                      {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
                    </div>
                  )}
                </>
              )}

              {/* OTP Section */}
              {authMethod !== 'password' && (
                <div className="otp-section animate-in">
                  {!isOtpSent ? (
                    <button
                      type="button"
                      onClick={handleSendOtp}
                      className="send-otp-btn"
                      disabled={isLoading}
                    >
                      {isLoading ? <Spinner /> : 'Send OTP'}
                    </button>
                  ) : (
                    <>
                      <div className="otp-inputs">
                        {formData.otp.map((digit, index) => (
                          <input
                            key={index}
                            id={`otp-${index}`}
                            type="text"
                            maxLength="1"
                            value={digit}
                            onChange={(e) => handleOtpChange(index, e.target.value)}
                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                            className="otp-input"
                          />
                        ))}
                      </div>
                      <div className="resend-otp">
                        {timer > 0 ? (
                          <span>Resend OTP in {timer}s</span>
                        ) : (
                          <button
                            type="button"
                            onClick={handleSendOtp}
                            className="resend-btn"
                          >
                            Resend OTP
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Remember Me & Forgot Password */}
              {isLogin && authMethod === 'password' && (
                <div className="form-options">
                  <label className="checkbox-label">
                    <input type="checkbox" />
                    <span>Remember me</span>
                  </label>
                  <a href="/#" className="forgot-link">Forgot Password?</a>
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className="submit-btn"
                disabled={isLoading || (authMethod !== 'password' && !isOtpSent)}
              >
                {isLoading ? (
                  <Spinner />
                ) : (
                  isLogin ? 'Sign In' : 'Sign Up'
                )}
              </button>

              {/* Social Login */}
              <div className="social-login">
                <div className="divider">
                  <span>OR</span>
                </div>
                <div className="social-buttons">
                  <button type="button" className="social-btn google">
                    <GoogleIcon />
                    Google
                  </button>
                  <button type="button" className="social-btn facebook">
                    <FacebookIcon />
                    Facebook
                  </button>
                </div>
              </div>

              {/* Switch Mode */}
              <div className="switch-mode">
                <span>
                  {isLogin ? "Don't have an account?" : "Already have an account?"}
                </span>
                <button type="button" onClick={switchMode} className="switch-btn">
                  {isLogin ? 'Sign Up' : 'Sign In'}
                </button>
              </div>
            </form>
          </div>

          {/* Right Side - Illustration */}
          <div className="auth-illustration">
            <div className="illustration-content">
              <div className="floating-shapes">
                <div className="shape shape-1"></div>
                <div className="shape shape-2"></div>
                <div className="shape shape-3"></div>
              </div>
              <h3>{isLogin ? 'Hello, Friend!' : 'Welcome Back!'}</h3>
              <p>
                {isLogin
                  ? 'Enter your personal details and start your journey with us'
                  : 'To keep connected with us please login with your personal info'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Icon Components
const PasswordIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
    <path d="M7 11V7a5 5 0 0110 0v4"/>
  </svg>
);

const EmailIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
    <polyline points="22,6 12,13 2,6"/>
  </svg>
);

const PhoneIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
  </svg>
);

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);

const FacebookIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="#1877F2">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const Spinner = () => (
  <div className="spinner">
    <div className="spinner-circle"></div>
  </div>
);

export default AuthScreen;